import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Visual rule builder — shared component that replaces the free-text
 * expression inputs across the admin (rule-set modal, inline add-rule,
 * template+rule-set create flow).
 *
 * The caller owns the array of rows (value / onChange); this file only
 * renders the controls for each row and exposes RuleRowEditor for the
 * single-row inline flow. All parse/serialize/format logic lives in
 * ./model so this file stays pure UI.
 */
import React, { useMemo } from 'react';
import { Button, Input, InputNumber, Radio, Select, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { emptyRow, validateRow, variablesInRow, } from './model';
const { Text } = Typography;
// --- Options catalogues ----------------------------------------------------
const TYPE_OPTIONS = [
    { value: 'range', label: 'טווח' },
    { value: 'comparison', label: 'השוואה' },
    { value: 'divisibility', label: 'התחלקות' },
    { value: 'advanced', label: 'מתקדם' },
];
const COMPARISON_OPS = [
    { value: '>', label: 'גדול מ-' },
    { value: '>=', label: 'גדול או שווה ל-' },
    { value: '==', label: 'שווה ל-' },
    { value: '!=', label: 'שונה מ-' },
    { value: '<', label: 'קטן מ-' },
    { value: '<=', label: 'קטן או שווה ל-' },
];
const DIV_MODES = [
    { value: 'even', label: 'זוגי' },
    { value: 'odd', label: 'אי-זוגי' },
    { value: 'divisibleBy', label: 'מתחלק ב-' },
];
// --- Variable Select -------------------------------------------------------
function VariableSelect({ value, onChange, variables, known, style }) {
    const options = useMemo(() => variables.map((v) => ({
        value: v,
        label: known.has(v) ? v : `${v} (לא קיים בתבנית)`,
    })), [variables, known]);
    // If the current value isn't in the options list yet (e.g. a parsed row
    // referenced a name later removed from the prompt), prepend it so the
    // dropdown doesn't drop the stored selection.
    const withSelected = value && !variables.includes(value)
        ? [{ value, label: `${value} (לא קיים בתבנית)` }, ...options]
        : options;
    return (_jsx(Select, { value: value || undefined, onChange: onChange, options: withSelected, placeholder: "\u05DE\u05E9\u05EA\u05E0\u05D4", style: { minWidth: 110, ...style } }));
}
// --- Per-type controls -----------------------------------------------------
function RangeControls({ row, onChange, variables, known }) {
    return (_jsxs(_Fragment, { children: [_jsx(VariableSelect, { value: row.variable, onChange: (v) => onChange({ ...row, variable: v }), variables: variables, known: known }), _jsx(Text, { children: "\u05D1\u05D9\u05DF" }), _jsx(InputNumber, { value: row.min === '' ? null : Number(row.min), onChange: (v) => onChange({ ...row, min: v == null ? '' : String(v) }), style: { width: 100 } }), _jsx(Text, { children: "\u05DC-" }), _jsx(InputNumber, { value: row.max === '' ? null : Number(row.max), onChange: (v) => onChange({ ...row, max: v == null ? '' : String(v) }), style: { width: 100 } })] }));
}
function RhsControls({ kind, value, onKindChange, onValueChange, variables, known }) {
    return (_jsxs(_Fragment, { children: [_jsx(Radio.Group, { value: kind, onChange: (e) => onKindChange(e.target.value), optionType: "button", size: "middle", options: [
                    { value: 'number', label: 'מספר' },
                    { value: 'variable', label: 'משתנה' },
                ] }), kind === 'number' ? (_jsx(InputNumber, { value: value === '' ? null : Number(value), onChange: (v) => onValueChange(v == null ? '' : String(v)), style: { width: 100 } })) : (_jsx(VariableSelect, { value: value, onChange: onValueChange, variables: variables, known: known }))] }));
}
function ComparisonControls({ row, onChange, variables, known }) {
    return (_jsxs(_Fragment, { children: [_jsx(VariableSelect, { value: row.variable, onChange: (v) => onChange({ ...row, variable: v }), variables: variables, known: known }), _jsx(Select, { value: row.op, onChange: (v) => onChange({ ...row, op: v }), options: COMPARISON_OPS, style: { minWidth: 160 } }), _jsx(RhsControls, { kind: row.rhsKind, value: row.rhsValue, onKindChange: (k) => onChange({ ...row, rhsKind: k, rhsValue: '' }), onValueChange: (v) => onChange({ ...row, rhsValue: v }), variables: variables, known: known })] }));
}
function DivisibilityControls({ row, onChange, variables, known }) {
    return (_jsxs(_Fragment, { children: [_jsx(VariableSelect, { value: row.variable, onChange: (v) => onChange({ ...row, variable: v }), variables: variables, known: known }), _jsx(Select, { value: row.mode, onChange: (mode) => {
                    if (mode === 'divisibleBy') {
                        onChange({ ...row, mode, divisorKind: row.divisorKind ?? 'number', divisorValue: row.divisorValue ?? '' });
                    }
                    else {
                        onChange({ kind: 'divisibility', variable: row.variable, mode });
                    }
                }, options: DIV_MODES, style: { minWidth: 140 } }), row.mode === 'divisibleBy' && (_jsx(RhsControls, { kind: row.divisorKind ?? 'number', value: row.divisorValue ?? '', onKindChange: (k) => onChange({ ...row, divisorKind: k, divisorValue: '' }), onValueChange: (v) => onChange({ ...row, divisorValue: v }), variables: variables, known: known }))] }));
}
function AdvancedControls({ row, onChange }) {
    return (_jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsx(Input, { value: row.expression, onChange: (e) => onChange({ expression: e.target.value, kind: 'advanced' }), placeholder: "(a * b) % 2 == 0", style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' } }), _jsx(Text, { type: "secondary", style: { fontSize: 11, display: 'block', marginTop: 2 }, children: "\u05D1\u05D9\u05D8\u05D5\u05D9 \u05D7\u05D5\u05E7 \u05D2\u05D5\u05DC\u05DE\u05D9 \u05DC\u05E4\u05D9 \u05D4\u05EA\u05D7\u05D1\u05D9\u05E8 \u05E9\u05DC \u05D4\u05DE\u05E2\u05E8\u05DB\u05EA, \u05DC\u05D3\u05D5\u05D2\u05DE\u05D4: a >= 2 && a <= 9" })] }));
}
// --- Single-row editor (also exported for the inline add form) -------------
export function RuleRowEditor({ row, onChange, onRemove, variables, serverError }) {
    // Expand the variable options with anything the row references, so a
    // parsed-but-unknown name still appears in the dropdown.
    const allVariables = useMemo(() => {
        const set = new Set(variables);
        for (const v of variablesInRow(row))
            if (v)
                set.add(v);
        return [...set];
    }, [variables, row]);
    const known = useMemo(() => new Set(variables), [variables]);
    const handleTypeChange = (kind) => {
        const currentVar = row.kind !== 'advanced' ? row.variable : (variables[0] ?? '');
        switch (kind) {
            case 'range':
                onChange({ kind: 'range', variable: currentVar, min: '', max: '' });
                return;
            case 'comparison':
                onChange({ kind: 'comparison', variable: currentVar, op: '>=', rhsKind: 'number', rhsValue: '' });
                return;
            case 'divisibility':
                onChange({ kind: 'divisibility', variable: currentVar, mode: 'even' });
                return;
            case 'advanced':
                onChange({ kind: 'advanced', expression: '' });
        }
    };
    const localError = validateRow(row);
    const errorMessage = serverError ?? localError;
    const hasServerError = !!serverError;
    return (_jsxs("div", { style: {
            border: hasServerError ? '1px solid #ff4d4f' : '1px solid #f0f0f0',
            borderRadius: 10,
            padding: '10px 12px',
            background: hasServerError ? '#fff1f0' : '#fafafa',
        }, children: [_jsxs("div", { style: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }, children: [_jsx(Select, { value: row.kind, onChange: handleTypeChange, options: TYPE_OPTIONS, style: { width: 110, flex: '0 0 auto' } }), row.kind === 'range' && (_jsx(RangeControls, { row: row, onChange: onChange, variables: allVariables, known: known })), row.kind === 'comparison' && (_jsx(ComparisonControls, { row: row, onChange: onChange, variables: allVariables, known: known })), row.kind === 'divisibility' && (_jsx(DivisibilityControls, { row: row, onChange: onChange, variables: allVariables, known: known })), row.kind === 'advanced' && (_jsx(AdvancedControls, { row: row, onChange: onChange })), onRemove && (_jsx(Button, { danger: true, icon: _jsx(DeleteOutlined, {}), onClick: onRemove, style: { marginInlineStart: 'auto', flex: '0 0 auto' } }))] }), errorMessage && (_jsx(Text, { type: "danger", style: { display: 'block', marginTop: 6, fontSize: 12 }, children: errorMessage }))] }));
}
// --- Multi-row builder (the primary export) --------------------------------
export function RuleBuilder({ rows, onChange, variables, serverErrors }) {
    const addRow = () => onChange([...rows, emptyRow(variables)]);
    const updateRow = (i, r) => {
        const next = rows.slice();
        next[i] = r;
        onChange(next);
    };
    const removeRow = (i) => {
        const next = rows.slice();
        next.splice(i, 1);
        onChange(next);
    };
    return (_jsxs("div", { children: [rows.map((row, i) => (_jsxs(React.Fragment, { children: [i > 0 && (_jsx("div", { style: {
                            textAlign: 'center',
                            color: '#8B2FD6',
                            fontWeight: 500,
                            fontSize: 12,
                            padding: '6px 0',
                            letterSpacing: 1,
                        }, children: "\u05D5\u05D2\u05DD" })), _jsx(RuleRowEditor, { row: row, onChange: (r) => updateRow(i, r), onRemove: () => removeRow(i), variables: variables, serverError: serverErrors?.[i] })] }, i))), rows.length === 0 && (_jsx(Text, { type: "secondary", style: { display: 'block', marginBottom: 8 }, children: "\u05E2\u05D3\u05D9\u05D9\u05DF \u05D0\u05D9\u05DF \u05D7\u05D5\u05E7\u05D9\u05DD. \u05DC\u05D7\u05E6\u05D5 \u05E2\u05DC \"\u05D4\u05D5\u05E1\u05E3 \u05D7\u05D5\u05E7\" \u05DB\u05D3\u05D9 \u05DC\u05D4\u05EA\u05D7\u05D9\u05DC." })), _jsx(Button, { type: "dashed", icon: _jsx(PlusOutlined, {}), onClick: addRow, block: true, style: { marginTop: 10 }, children: "\u05D4\u05D5\u05E1\u05E3 \u05D7\u05D5\u05E7" })] }));
}
